import { get } from "@/api/public.client";
import EventDate from "@/components/public/event-date";
import EventDescription from "@/components/public/event-description";
import EventLocation from "@/components/public/event-location";
import EventThemeApplyer from "@/components/public/event-theme-applyer";
import EventThemeEditor from "@/components/public/event-theme-editor";
import EventTickets from "@/components/public/event-tickets";
import EventTicketsCart from "@/components/public/event-tickets-cart";
import { MetaHead } from "@/components/public/meta-head";
import { OrganizationBadge } from "@/components/public/organization-badge";
import Poster from "@/components/public/poster";
import { Umami } from "@/components/public/umami";
import { Button } from "@/components/ui/button";
import { safeJsonParse } from "@/lib/safeJsonParse";
import FirebaseWrapper from "@/lib/useFirebaseNotifications";
import { cn } from "@/lib/utils";
import { Ticket as TicketType } from "@/types/prismaTypes";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { useLocalStorage, useMediaQuery, useTimeout } from "usehooks-ts";
import EventContainerManageCard from "./event-container-manage-event";
import getTextFromJSON from "@/lib/tiptap-json-to-text";
import { Dialog, DialogTrigger, DialogContent } from "../ui/dialog";
import { ImageWithFallback } from "../ui/image-with-fallback";
import { LinkIcon } from "lucide-react";

export default function EventContainer({ event, className, inSheet }: any) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useTranslations("EventContainer");

  const [localStorageCart, setLocalStorageCart] = useLocalStorage("cart", {});

  const [tickets, setTickets] = useState<TicketType[]>([]);
  const [remainingQuantity, setRemainingQuantity] = useState([]);
  const [hiddenTickets, setHiddenTickets] = useState<Boolean>(false);
  const [accessCode, setAccessCode] = useState<any>("");
  const [accessCodes, setAccessCodes] = useState<any>([]);
  const [refreshTickets, setRefreshTickets] = useState<Boolean>(false);
  const [cart, setCart] = useState<Record<string, number>>();
  const [checkoutData, setCheckoutData] = useState<any>();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [containerVisibiliy, setContainerVisibility] = useState(false);
  const [backgroundVisibiliy, setBackgroundVisibility] = useState(false);

  useTimeout(() => setContainerVisibility(true), isDesktop ? 400 : 800);
  useTimeout(() => setBackgroundVisibility(true), isDesktop ? 100 : 200);

  useEffect(() => {
    let ticketsInCart: any = [];
    cart &&
      Object.keys(cart) &&
      Object.keys(cart).map((item) => {
        for (let i = 0; i < cart[item]; i++) {
          ticketsInCart.push(tickets.find((t) => String(t.id) == item));
        }
      });

    setCheckoutData({
      events: [
        {
          id: event?.id,
          cart,
          ticketsInCart,
          approvalRequired: event?.approvalRequired,
          helperMessages: event?.helperMessages,
          cashAccepted: event?.cashAccepted,
          cardAccepted: event?.cardAccepted,
          additionalCss: event?.additionalCss,
          organization: event?.organization,
        },
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  function updateCartCallback(id: string, quantity: number) {
    if (id !== undefined && quantity !== undefined) {
      let tempCart = { ...cart };
      tempCart[id] = quantity;
      setCart(tempCart);
    }
  }

  function validateCart() {
    setLocalStorageCart(checkoutData);
    const ref = searchParams?.get("ref");
    let redirectUrl =
      event.seatSettings && Object.keys(event.seatSettings).length > 0
        ? "/events/" + event.id + "/seat-selection"
        : "/events/" + event.id + "/checkout";
    if (ref) {
      redirectUrl += "?ref=" + ref;
    }
    router.push(redirectUrl);
  }

  useEffect(() => {
    get("/public/tickets", {
      params: {
        query: {
          eventId: event?.id,
          accessCodes: accessCodes,
          projectionId: "",
        },
      },
    }).then((fetchResponse: any) => {
      if (fetchResponse.error) {
        window.dispatchEvent(new CustomEvent("wrong-access-code"));
      } else {
        setTickets(fetchResponse.data.tickets);
        setHiddenTickets(fetchResponse.data.hiddenTicketsAvailable);
        setRemainingQuantity(fetchResponse.data.remainingQuantity);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, accessCodes]);

  useEffect(() => {
    accessCode && setAccessCodes(accessCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTickets]);

  return (
    <>
      {!inSheet ? (
        <EventThemeEditor event={event} additionalCss={event?.additionalCss} />
      ) : (
        <div
          className={cn(
            !isDesktop && "opacity-0",
            "transition-opacity duration-500",
            backgroundVisibiliy && "opacity-100",
          )}
        >
          <EventThemeApplyer
            event={event}
            theme={safeJsonParse(event?.theme)}
            additionalCss={event?.additionalCss}
            inSheet={inSheet}
          />
        </div>
      )}

      <MetaHead
        title={event.name + " - Tunis.events"}
        description={
          getTextFromJSON(event.description).substring(0, 160) + "..."
        }
        url={process.env.BASE_UI_URL + "/events/" + event.id}
        image={
          process.env.BASE_UI_URL + "/api/opengraph-image?eventId=" + event.id
        }
      />
      <Umami umamiWebsiteId={event.organization?.umamiSettings.umamiWebsite} />
      <div
        className={cn(
          className,
          "opacity-0 transition-opacity duration-500 z-50 flex flex-wrap items-start w-full gap-4 p-4 container max-w-5xl h-full content-start",

          containerVisibiliy ? "opacity-100" : "",
          !inSheet && "md:flex-nowrap",
        )}
        id="ev-container"
      >
        <div
          className={cn(
            "w-full gap-4  rounded-lg flex justify-center flex-col ",
            !inSheet
              ? "md:w-1/3 bg-background/0 "
              : "content-center items-center px-4",
          )}
        >
          <Dialog>
            <DialogTrigger disabled={!isDesktop}>
              <Poster
                event={event}
                id="ev-poster"
                className={cn(
                  "rounded-2xl hover:scale-[101%] transition-all",
                  isDesktop && "cursor-pointer max-h-none max-w-full",
                  inSheet &&
                    "max-h-80 max-w-80 items-center aspect-square shadow-2xl",
                )}
              ></Poster>
            </DialogTrigger>
            <DialogContent className="p-0 overflow-hidden md:rounded-2xl max-w-fit max-h-fit">
              <ImageWithFallback src={event.image} />
            </DialogContent>
          </Dialog>
          <EventContainerManageCard event={event} />
          <div
            className={cn("hidden md:flex", inSheet && "md:hidden")}
            id="ev-organization-badge"
          >
            {event.organization && (
              <OrganizationBadge organization={event.organization} />
            )}
          </div>
        </div>
        <div
          className={cn(
            "w-full flex flex-wrap s flex-col gap-4 md:p-2 md:px-4 rounded-lg",
            !inSheet && "md:w-2/3 bg-background/0 backdrop-blur",
          )}
          id="ev-main"
        >
          <div
            className="text-2xl font-semibold leading-tight tracking-tighter md:text-4xl lg:leading-[1.1]  flex flex-row gap-4 items-center"
            id="ev-name"
          >
            <h1 className="first-letter:uppercase">{event.name} </h1>
            {event.link && (
              <Link href={event.link} target="_blank" rel="noopener noreferrer">
                <LinkIcon className="transition-opacity opacity-30 hover:opacity-100" />
              </Link>
            )}
          </div>

          {event.organization && (
            <div
              id="ev-organization-badge"
              className={cn(
                "flex flex-row items-center space-x-2 md:hidden",
                inSheet && "md:flex",
              )}
            >
              <OrganizationBadge organization={event.organization} />
            </div>
          )}

          <div className="flex flex-col gap-1">
            {!event.hasProjections && <EventDate event={event} id="ev-date" />}
            <EventLocation event={event} id="ev-location" />
          </div>
          {!inSheet && (tickets.length > 0 || hiddenTickets) && (
            <div className="w-full p-2 pt-1 space-y-2 border-none rounded-xl bg-background/20">
              <h1 className="mt-1 ml-1 font-semibold tracking-tight ">
                {t("get-access")}
              </h1>
              <EventTickets
                remainingQuantity={remainingQuantity}
                tickets={tickets}
                updateCartCallback={updateCartCallback}
                setAccessCode={setAccessCode}
                accessCode={accessCode}
                cart={cart}
                hiddenTickets={hiddenTickets as any}
                refreshTickets={refreshTickets as any}
                setRefreshTickets={setRefreshTickets}
                // id="ev-tickets"
                hidePrice={event.preApprovalStage}
                groupByCategory={true}
              />
              <EventTicketsCart
                step="Cart"
                cart={cart}
                tickets={tickets}
                validateCart={validateCart}
                validateCartForm={() => {
                  return true;
                }}
                id="ev-cart"
              />
            </div>
          )}
          {inSheet &&
            (tickets.length > 0 || hiddenTickets || event.hasProjections) && (
              <div
                className="w-full p-2 pt-1 space-y-2 border-none rounded-xl bg-background/20"
                id="ev-hidden-tickets"
              >
                <h1 className="mt-1 ml-1 font-semibold tracking-tight ">
                  {t("get-access")}
                </h1>
                <Button
                  className="w-full text-lg"
                  size={"lg"}
                  variant={"default"}
                  asChild
                >
                  <Link href={process.env.BASE_UI_URL + "/events/" + event.id}>
                    {t("book-online")}
                  </Link>
                </Button>
              </div>
            )}
          <EventDescription event={event} id="ev-description" />

          <EventLocation withMap={true} event={event} />
        </div>
      </div>

      <FirebaseWrapper
        topics={[`E-${event.id}`, `ORG-${event.organizationId}`, `GENERAL`]}
      />
    </>
  );
}
